import BaseTypeController from "vamos/cockpit/base/controller/type/BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import Dialog from "sap/m/Dialog";
import V4Context from "sap/ui/model/odata/v4/Context";
import ODataModel from "sap/ui/model/odata/v4/ODataModel";
import Filter from "sap/ui/model/Filter";
import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import FilterOperator from "sap/ui/model/FilterOperator";
import {GoodsRecipient, MeasurementUnit, Product, SupplierCondition} from "vamos/cockpit/model/odata/VamosModel";
import {SupplierConditionDto} from "vamos/cockpit/data/supplier/condition/dto/SupplierConditionDto";
import ValueHelp from "sap/ui/mdc/ValueHelp";
import GoodsRecipientService from "vamos/cockpit/data/supplier/service/GoodsRecipientService";
import MessageBox from "sap/m/MessageBox";
import TimeUtil from "vamos/cockpit/util/TimeUtil";
import PackingUnitService from "vamos/cockpit/data/packingunit/service/PackingUnitService";
import MeasurementUnitService from "vamos/cockpit/data/measurementunit/service/MeasurementUnitService";
import Field, {Field$ChangeEvent} from "sap/ui/mdc/Field";
import ValidationUtil from "vamos/cockpit/base/util/ValidationUtil";

export default class CreateEditSupplierConditionDialogController extends BaseTypeController {
    private dataModel: JSONModel
    private bindingContext: V4Context;
    private dialog: Dialog;
    private supplierConditionDtos: SupplierConditionDto[]
    private packingUnitVh: ValueHelp;
    private goodsRecipientService: GoodsRecipientService;
    private packingUnitService: PackingUnitService;
    private measurementUnitService: MeasurementUnitService;

    private callBack: ((succeededGuids: string[]) => void) | null = null;
    private currentItemIndex: number;
    private succeededGuids: string[] = []


    onInit() {
        super.onInit();
        this.goodsRecipientService = new GoodsRecipientService(this.getComponent());
        this.packingUnitService = new PackingUnitService(this.getComponent());
        this.measurementUnitService = new MeasurementUnitService(this.getComponent());
        this.dataModel = new JSONModel();
        this.dialog = this.getView().byId("supplierConditionDialog") as Dialog;
        this.setModel(this.dataModel, "Variant");
        this.packingUnitVh = this.getView().byId("packingUnit-vh") as ValueHelp;
        this.goodsRecipientService = new GoodsRecipientService(this.getComponent());


    }

    public openDialog(data: SupplierConditionDto[]) {
        if (data.length === 0) {
            console.error("No data provided");
            return;
        }
        this.setSupplierConditionDtos(data);
        this.dialog.open();
    }

    public getDataModel() {
        return this.dataModel;
    }

    public getDialog(): Dialog {
        return this.dialog;
    }

    public async onAfterDialogOpened() {
        this.initialize();
        const firstItem: SupplierConditionDto = this.getCurrentItem();
        await this.processData(firstItem);

    }

    public onAfterDialogClosed() {
        if (this.callBack) {
            this.callBack(this.succeededGuids);
        }
    }

    public async processData(dto: SupplierConditionDto): Promise<void> {
        this.mapDtoToJsonModel(dto);
        const saveButtonText: string = this.isLastItem() ? this.getText("Save") : this.getText("SaveAndNext");
        this.getViewModel().setProperty("/saveButtonText", saveButtonText);
        this.initializeBaseMeasurementUnit()


    }

    private async initializeBaseMeasurementUnit(): Promise<void> {
        const model: ODataModel = this.getModel() as ODataModel;
        const filter: Filter = new Filter("SapCode", FilterOperator.EQ, "01");
        const binding: ODataListBinding = model.bindList("/MeasurementUnits", undefined, undefined, filter);
        const contexts: V4Context[] = await binding.requestContexts();
        const entries: MeasurementUnit = contexts.map((context) => {
            const measurementUnit: MeasurementUnit = context.getObject() as MeasurementUnit;
            return {
                Guid: "",
                MeasurementUnitName: measurementUnit.Name,
                Factor: 1,
                BaseUnitName: "Meter",
            }
        });
        (this.getModel("ValueHelp") as JSONModel).setProperty("/PackingUnits", entries);
    }

    public async supplierChanged(supplierGuid: string, articleGuid: string) {
        await this.loadGoodsRecipients(supplierGuid);
        const defaultGoodsRecipientGuid: string = await this.goodsRecipientService.callGetDefaultForArticleAndSupplierGuid(articleGuid, supplierGuid);
        this.setDefaultGoodsRecipientGuid(defaultGoodsRecipientGuid);
        return defaultGoodsRecipientGuid;


    }

    public async loadGoodsRecipients(supplierGuid: string) {
        let contexts: V4Context[] | null;
        if (supplierGuid) {
            contexts = await this.goodsRecipientService.loadBySupplierGuid(supplierGuid);
        } else {
            contexts = await this.goodsRecipientService.loadAll();
        }
        const goodsRecipient: GoodsRecipient[] = contexts.map(context => context.getObject() as GoodsRecipient) ?? [];
        this.setGoodsRecipients(goodsRecipient);
    }

    public onResetGoodsRecipient() {
        const model: JSONModel = this.getDataModel();
        const defaultGoodsRecipientGuid: string = model.getProperty("/defaultGoodsRecipientGuid") as string;
        model.setProperty("/supplierCondition/GoodsRecipientGuid", defaultGoodsRecipientGuid);
    }

    async onSaveSupplierCondition(): Promise<void> {
        const dto: SupplierConditionDto = this.getCurrentItem() as SupplierConditionDto;
        let context: V4Context | null = dto.getSupplierConditionContext();

        if (!context) {
            context = this.handleNewData();
            dto.setSupplierConditionContext(context)
        }

        const data: SupplierCondition = this.getDataModel().getProperty("/supplierCondition") as SupplierCondition;
        if (ValidationUtil.hasEmptyRequiredFields(this.getDialog())) {
            return;
        }
        this.mapJsonToOdata(data, context);

        await (this.getModel() as ODataModel).submitBatch("change");
        if (!context.hasPendingChanges()) {
            this.addSucceedItem(dto.getOriginGuid);

            if (this.isLastItem()) {
                this.closeDialog();
            } else {
                const nextEntry: SupplierConditionDto = this.nextEntry();
                this.processData(nextEntry);

            }
        }
    }

    onClose() {
        this.closeDialog();
    }

    private closeDialog() {
        this.dialog.close();
    }

    mapJsonToOdata(oJsonModelObject: object, context: V4Context, sGroupId: string = "change") {
        const oMetaModel = this.getModel().getMetaModel();
        for (const sParameterName in oJsonModelObject) {
            const sType: any = oMetaModel.getObject(`/SupplierConditions/${sParameterName}/$Type`) as any;
            let sValue = oJsonModelObject[sParameterName];
            sValue = sValue === "" ? null : sValue;
            switch (sType) {
                case "Edm.DateTimeOffset":
                    sValue = TimeUtil.formatIntoV4Date(sValue);
                    break;
                case "Edm.Int32":
                    if (typeof sValue === "string") {
                        sValue = parseInt(sValue);
                    }
                    break;
                case "Edm.Double":
                    if (typeof sValue === "string") {
                        sValue = parseFloat(sValue);
                    }
                    break;
                default:
                    break;
            }
            void context.setProperty(sParameterName, sValue, sGroupId, false);
        }
    }

    handleNewData() {
        return this.createModelEntry("SupplierCondition", {}, null, "change") as V4Context;
    }

    private initialize(): void {
        this.currentItemIndex = 0;
        this.succeededGuids = [];
        this.updateCounter();
        this.getViewModel().setProperty("/totalEntries", this.supplierConditionDtos.length);

    }

    private mapDtoToJsonModel(dto: SupplierConditionDto) {
        const model: JSONModel = this.getDataModel();
        model.setProperty("/supplierCondition", {});
        model.setProperty("/MaterialGroupName", dto.getMaterialGroupName());
        model.setProperty("/ArticleName", dto.getArticleName());
        model.setProperty("/ArticleNumber", dto.getArticleNumber());
        model.setProperty("/ProductName", dto.getProductName());
        model.setProperty("/SupplierCondition/BaseUnitGuid", dto.getBaseUnitGuid());

        if (dto.getSupplierConditionContext()) {
            model.setProperty("/supplierCondition", dto.getSupplierCondition());
        } else {
            model.setProperty("/supplierCondition/SupplierName", dto.getSupplierName());
            model.setProperty("/supplierCondition/SupplierGuid", dto.getSupplierGuid());
            model.setProperty("/supplierCondition/ProductGuid", dto.getProductGuid());
            model.setProperty("/supplierCondition/DefaultPriceUnitGuid", dto.getBaseUnitGuid());
            model.setProperty("/supplierCondition/DefaultPriceUnitName", dto.getBaseUnitName());
        }
    }

    public async onSupplierChange(event: Field$ChangeEvent) {
        const field: Field = event.getSource();
        const supplierGuid = field.getValue() as string;
        const dto: SupplierConditionDto = this.getCurrentItem();
        const articleGuid: string = dto.getArticleGuid();

        const defaultGoodsRecipientGuid = await this.supplierChanged(supplierGuid, articleGuid);
        this.getDataModel().setProperty("/supplierCondition/GoodsRecipientGuid", defaultGoodsRecipientGuid);
    }

    public setCloseCallback(callback: (() => void)) {
        this.callBack = callback;

    }

    private setSupplierConditionDtos(supplierConditionDtos: SupplierConditionDto[]): void {
        this.supplierConditionDtos = supplierConditionDtos;

    }

    private setGoodsRecipients(goodsRecipients: GoodsRecipient[]): void {
        this.getDataModel().setProperty("/goodsRecipients", goodsRecipients);

    }

    private setDefaultGoodsRecipientGuid(defaultGuid: string): void {
        this.getDataModel().setProperty("/defaultGoodsRecipientGuid", defaultGuid);
    }

    private getCurrentItem(): SupplierConditionDto {
        return this.supplierConditionDtos[this.currentItemIndex] as SupplierConditionDto;
    }

    private nextEntry(): SupplierConditionDto {
        this.currentItemIndex++;
        this.updateCounter();
        return this.getCurrentItem();
    }

    private updateCounter(): void {
        this.getViewModel().setProperty("/currentIndex", this.currentItemIndex + 1);
    }

    private isLastItem(): boolean {
        return this.currentItemIndex === this.supplierConditionDtos.length - 1;
    }

    private addSucceedItem(guid: string): void {
        this.succeededGuids.push(guid);
    }

}
